import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import Retracked from 'js/app/retracked';
import initBem from 'js/lib/bem';

import PasteDeterrenceNotification from 'bundles/academic-integrity/components/PasteDeterrenceNotification';
import PlagiarismDetectionContainer, {
  ASSIGNMENT_TYPES,
} from 'bundles/academic-integrity/components/plagiarism-detection/PlagiarismDetectionContainer';
import { getSimilarityCheckRoleFromAssignmentRole } from 'bundles/academic-integrity/components/plagiarism-detection/utils/SimilarityCheckRoles';
import RichTextView from 'bundles/assess-common/components/RichTextView';
import type { AssignmentRole } from 'bundles/assignment-common/types/AssignmentRoles';
import CMLEditor from 'bundles/authoring/content-authoring/components/CMLEditor';
import type { CmlContent, HtmlContent } from 'bundles/cml';
import { HTMLToCMLConverter, Tools } from 'bundles/cml';
import type {
  CmlContentType,
  Content,
  HtmlContentType,
} from 'bundles/compound-assessments/components/api/types/CompoundAssessmentsForm';
import ValidationError from 'bundles/compound-assessments/components/form-parts/ValidationError';
import type { FormPartsValidationStatus } from 'bundles/compound-assessments/components/form-parts/lib/checkResponsesInvalid';
import { FormPartsValidationStatuses } from 'bundles/compound-assessments/components/form-parts/lib/constants';
import { typeNames } from 'bundles/compound-assessments/constants';
import type { RichTextPrompt, RichTextResponse } from 'bundles/compound-assessments/types/FormParts';
import AcademicIntegrityExperiments from 'bundles/epic/clients/AcademicIntegrity';

import 'css!./__styles__/RichText';

import ReadOnlyText from '../ReadOnlyText';

const bem = initBem('RichText');

const DEFAULT_DTD_ID = 'assess/1';

type Props = {
  prompt: RichTextPrompt;
  response: RichTextResponse;
  onChangeResponse: (response: RichTextResponse) => void;
  isReadOnly: boolean;
  isDisabled: boolean;
  isAutoSaving: boolean;
  showValidation: boolean;
  ariaLabelledBy?: string;
  itemId: string;
  userId: number;
  courseId: string;
  responseId: string;
  role?: AssignmentRole;
  teamId?: string;
};

type State = {
  hasUnsavedChanges: boolean;
  showPastingNotification: boolean;
};

export const checkInvalid = (response: RichTextResponse): FormPartsValidationStatus | null => {
  const definition = (((response || {}).definition || {}).submissionPartResponse || {}).definition || {};

  const isValid =
    (((definition.richText || {}).definition || {}) as HtmlContentType).html ||
    (((definition.richText || {}).definition || {}) as CmlContentType).value;
  return !isValid ? FormPartsValidationStatuses.warning : null;
};

/**
 * Issues with a11y implementation not possible to add custom aria attributes
 * @see https://github.com/ianstormtaylor/slate/issues/2572
 */
class RichText extends React.Component<Props, State> {
  declare htmlToCmlConverter: HTMLToCMLConverter;

  static contextTypes = {
    _eventData: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof RichText.contextTypes>;

  constructor(props: Props) {
    super(props);
    this.htmlToCmlConverter = new HTMLToCMLConverter();
  }

  state = {
    hasUnsavedChanges: false,
    showPastingNotification: false,
  };

  onChange = (value: CmlContent) => {
    const { onChangeResponse } = this.props;

    const { definition } = value;
    const newRichText: CmlContentType = {
      value: definition.value,
      dtdId: definition.dtdId || DEFAULT_DTD_ID,
    };

    const newResponse = {
      typeName: typeNames.SUBMISSION_RESPONSE,
      definition: {
        submissionPartResponse: {
          typeName: 'richTextResponse',
          definition: {
            richText: {
              typeName: 'cmlContent',
              definition: {
                ...newRichText,
              },
            },
          },
        },
      },
    } as RichTextResponse;

    this.setState({ hasUnsavedChanges: true });
    onChangeResponse(newResponse);
  };

  componentDidUpdate(prevProps: Props) {
    // Reset hasUnsavedChanges when manual saving is complete or when autosaving starts,
    // this will trigger an automatic similarity check if we are using v3.
    const { isDisabled, isAutoSaving } = this.props;
    const { hasUnsavedChanges } = this.state;
    if (hasUnsavedChanges && ((prevProps.isDisabled && !isDisabled) || (prevProps.isAutoSaving && !isAutoSaving))) {
      this.setState({ hasUnsavedChanges: false });
    }
  }

  selectRichTextType(richText: Content) {
    switch (richText.typeName) {
      case 'htmlContent':
        return {
          richText: {
            typeName: 'html',
            definition: richText.definition.html,
          } as HtmlContent,
        };
      case 'cmlContent':
        return {
          richText: {
            typeName: 'cml',
            definition: richText.definition,
          } as CmlContent,
        };
      default:
        return {
          richText: '',
        };
    }
  }

  render() {
    const {
      response,
      prompt,
      isReadOnly,
      showValidation,
      ariaLabelledBy,
      userId,
      courseId,
      itemId,
      responseId,
      role,
      teamId,
    } = this.props;
    const { showPastingNotification } = this.state;

    const defaultPlagiarismDetectionOn = AcademicIntegrityExperiments.get('defaultPlagiarismDetectionEnabled');

    if (!prompt) {
      return null;
    }

    const richTextContent = response?.definition?.submissionPartResponse?.definition?.richText;

    const content =
      (richTextContent?.definition as HtmlContentType)?.html ||
      (richTextContent?.definition as CmlContentType)?.value ||
      '';

    const isInvalid = showValidation && checkInvalid(response);

    const cmlContent: CmlContent = {
      typeName: 'cml',
      definition: {
        dtdId: (richTextContent?.definition as CmlContentType)?.dtdId || DEFAULT_DTD_ID,
        value:
          (richTextContent?.definition as CmlContentType)?.value || this.htmlToCmlConverter.toCML(content, true, true),
      },
    };

    // Warning: this is uncontrolled component. Changed props will not update text.
    return (
      <div className={bem()}>
        {isReadOnly ? (
          <ReadOnlyText isMultiLine={true}>
            {content && <RichTextView submissionPart={this.selectRichTextType(richTextContent)} />}
          </ReadOnlyText>
        ) : (
          <div
            className={bem('richText')}
            onPaste={(event) => {
              const clipboardTextLength = event.clipboardData?.getData('text').length;
              const { _eventData } = this.context;
              Retracked.trackComponent(
                _eventData,
                { clipboardTextLength, userId, partId: prompt.id },
                'pasting_detection',
                'paste'
              );
              this.setState({ showPastingNotification: true });
            }}
          >
            {showPastingNotification && (
              <div style={{ marginBottom: 8 }}>
                <PasteDeterrenceNotification onDismiss={() => this.setState({ showPastingNotification: false })} />
              </div>
            )}
            <CMLEditor
              initialCML={cmlContent}
              onContentChange={this.onChange}
              ariaLabelledBy={ariaLabelledBy}
              readOnly={isReadOnly}
              customTools={[
                Tools.BOLD,
                Tools.ITALIC,
                Tools.MATH,
                Tools.LINK,
                Tools.UNLINK,
                Tools.UNORDERED_LIST,
                Tools.ORDERED_LIST,
                Tools.SUBSCRIPT,
                Tools.SUPERSCRIPT,
                Tools.CODE,
                Tools.MONOSPACE,
              ]}
              borderColor={isInvalid ? 'var(--cds-color-feedback-error)' : undefined}
            />
            {isInvalid && <ValidationError />}
          </div>
        )}
        {prompt.plagiarismConfig?.plagiarismCheckEnable && defaultPlagiarismDetectionOn && (
          <PlagiarismDetectionContainer
            courseId={courseId}
            itemId={itemId}
            authorId={userId}
            assignmentType={ASSIGNMENT_TYPES.STAFF}
            responseId={responseId}
            hasContent={!!content}
            hasUnsavedChanges={this.state.hasUnsavedChanges}
            role={getSimilarityCheckRoleFromAssignmentRole(role)}
            teamId={teamId}
          />
        )}
      </div>
    );
  }
}

export default RichText;
